import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import {
  Sparkles, Award, ShoppingBag, Truck, Rocket, Copy, ExternalLink, Globe,
} from "lucide-react";
import api from "@/lib/api";
import CosmicBackground from "@/components/lifted/CosmicBackground";
import Brand from "@/components/lifted/Brand";
import { AFF } from "@/constants/testIds";

const TIER_COLOR = { "Lift-Off": "#8ee400", Orbit: "#b6ff1a", Galaxy: "#8b3fc2", Cosmos: "#f3f5f0" };

export default function PublicAffiliateHub() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, data: null });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/affiliate/public/${slug}`)
       .then((r) => setState({ loading: false, data: r.data }))
       .catch(() => setState({ loading: false, data: null }));
  }, [slug]);

  const logClick = (slot) => {
    api.post(`/affiliate/public/${slug}/click`, { source: slot, landing_url: window.location.href }).catch(() => {});
  };

  if (state.loading) {
    return (
      <>
        <CosmicBackground />
        <div className="min-h-screen flex items-center justify-center font-bebas tracking-[0.3em] text-lifted-dim">LOADING…</div>
      </>
    );
  }
  if (!state.data) {
    return (
      <>
        <CosmicBackground />
        <div className="min-h-screen flex items-center justify-center p-6">
          <div className="glass-panel p-10 max-w-md text-center">
            <Globe className="h-12 w-12 text-lifted-mute mx-auto" />
            <h1 className="font-anton text-3xl uppercase mt-4">Hub not found</h1>
            <p className="text-lifted-dim mt-2">No active Lifted affiliate matches <code className="text-lifted-text">{slug}</code>.</p>
          </div>
        </div>
      </>
    );
  }

  const d = state.data;
  const tColor = TIER_COLOR[d.tier] || "#b6ff1a";
  const c = d.training_credibility;

  const copyCode = async () => {
    try { await navigator.clipboard.writeText(d.referral_code); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch (e) { console.warn("[affiliate] clipboard copy denied", e); }
  };

  return (
    <>
      <CosmicBackground />
      <div data-testid={AFF.publicHub} className="min-h-screen px-4 sm:px-8 py-8 flex flex-col">
        <header className="flex items-center justify-between">
          <Brand to="/" />
          <a href="/auth/register" className="btn-ghost !py-2 !px-4 !text-xs">Join Lifted →</a>
        </header>

        <main className="flex-1 max-w-5xl mx-auto w-full mt-12 space-y-12">

          {/* Hero */}
          <section className="text-center fade-up">
            <div className="eyebrow mb-3">
              <span data-testid={AFF.publicTier} style={{ color: tColor }}>{d.tier}</span> · Lifted Affiliate
            </div>
            <h1 className="headline-xl">
              <span data-testid={AFF.publicName}>{d.name}</span>
            </h1>
            <p className="text-lifted-dim mt-4 max-w-2xl mx-auto text-base leading-relaxed">{d.tagline}</p>

            {/* Trust badges */}
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {c.affiliate_certified && (
                <span data-testid={AFF.publicCertBadge} className="chip chip--green !text-[0.7rem]">
                  <Award className="h-3 w-3" /> Lifted-Certified Affiliate
                </span>
              )}
              {c.badges.map((b) => (
                <span key={b.key} className="chip !text-[0.7rem]" style={{ background: `${b.color}25`, color: b.color, borderColor: b.color }}>
                  {b.label}
                </span>
              ))}
              {c.modules_total > 0 && (
                <span className="chip !text-[0.7rem]">
                  <Sparkles className="h-3 w-3" /> {c.modules_completed} / {c.modules_total} Lifted modules complete
                </span>
              )}
            </div>
          </section>

          {/* Code + QR */}
          <section className="glass-panel p-8 fade-up delay-1">
            <div className="grid md:grid-cols-3 gap-6 items-center">
              <div className="md:col-span-2">
                <div className="eyebrow !text-lifted-text-dim">My code</div>
                <div className="mt-2 font-anton text-5xl uppercase break-all" style={{ color: tColor }}>{d.referral_code}</div>
                <p className="text-sm text-lifted-dim mt-2">Mention this code at checkout — or use any button below.</p>
                <button onClick={copyCode} className="btn-lifted mt-5 !text-xs">
                  <Copy className="h-3 w-3" /> {copied ? "Copied" : "Copy code"}
                </button>
              </div>
              <div className="flex justify-center">
                <div className="p-3 bg-lifted-black rounded-xl border border-lifted-green/20" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(d.qr_svg || "", { USE_PROFILES: { svg: true, svgFilters: true } }) }} />
              </div>
            </div>
          </section>

          {/* Bio */}
          {d.bio && (
            <section className="glass-panel p-6 fade-up">
              <div className="eyebrow !text-lifted-text-dim">About</div>
              <p className="text-lifted-text mt-2 leading-relaxed">{d.bio}</p>
            </section>
          )}

          {/* Promotion ladder */}
          <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <PromoCard
              testid={AFF.publicCta("lifted_at_the_moment")}
              onClick={() => logClick("liftedatthemoment")}
              href={d.promote.lifted_at_the_moment}
              icon={ShoppingBag}
              label="Lifted At The Moment"
              blurb="Browse the shop with my code applied automatically."
              accent="#b6ff1a"
            />
            <PromoCard
              testid={AFF.publicCta("lifted_supplies")}
              onClick={() => logClick("lifted_supplies")}
              href={d.promote.lifted_supplies}
              icon={Truck}
              label="Lifted Supplies"
              blurb="Same-day delivery experience, attributed to me."
              accent="#8b3fc2"
            />
            <PromoCard
              testid={AFF.publicCta("affiliate_program")}
              onClick={() => logClick("affiliate_program")}
              href={d.promote.affiliate_program}
              icon={Rocket}
              label="Join the program"
              blurb="Become a Lifted Affiliate and build your own deck."
              accent="#f3f5f0"
            />
          </section>

          {/* Tier showcase */}
          <section className="glass-panel p-6 fade-up text-center">
            <div className="eyebrow !text-lifted-text-dim">Tier ladder</div>
            <div className="mt-3 flex flex-wrap justify-center gap-2">
              {["Lift-Off", "Orbit", "Galaxy", "Cosmos"].map((k) => (
                <span key={k} className="chip" style={{
                  background: d.tier === k ? `${TIER_COLOR[k]}25` : undefined,
                  color: d.tier === k ? TIER_COLOR[k] : undefined,
                  borderColor: d.tier === k ? TIER_COLOR[k] : undefined,
                }}>{k}</span>
              ))}
            </div>
            {d.tier_meta && (
              <p className="text-xs text-lifted-dim mt-3">
                {d.tier_meta.label} · {d.tier_meta.min_referrals}+ referrals
              </p>
            )}
          </section>
        </main>

        <footer className="text-center text-[0.65rem] tracking-[0.3em] font-bebas text-lifted-mute mt-12">
          LIFTED ECOSYSTEM · ONE LOGIN · ONE UNIVERSE
        </footer>
      </div>
    </>
  );
}

function PromoCard({ testid, href, onClick, icon: Icon, label, blurb, accent }) {
  return (
    <a data-testid={testid} href={href} onClick={onClick} target="_blank" rel="noreferrer"
       className="glass-panel p-6 block hover:-translate-y-1 transition-transform"
       style={{ borderColor: `${accent}40` }}>
      <div className="flex items-center justify-between">
        <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: `${accent}15`, color: accent, border: `1px solid ${accent}40` }}>
          <Icon className="h-5 w-5" />
        </div>
        <ExternalLink className="h-4 w-4 text-lifted-dim" />
      </div>
      <div className="mt-5">
        <div className="font-anton text-xl uppercase">{label}</div>
        <p className="text-sm text-lifted-dim mt-1.5">{blurb}</p>
      </div>
    </a>
  );
}
